import { useEffect, useState } from "react";
import axios from "axios";
import { LayoutDashboard, Loader2, LogOut, Package, RefreshCw, Users } from "lucide-react";
import { API, auth } from "@/components/admin/crm";
import { Dashboard } from "@/components/admin/Dashboard";
import { LeadsTable } from "@/components/admin/LeadsTable";
import { LeadDetail } from "@/components/admin/LeadDetail";
import { Orders } from "@/components/admin/Orders";
import { formatApiError } from "@/lib/vgt";

const TOKEN_KEY = "vgt_admin_token";

const loginInputCls =
  "w-full bg-[#FDFBF7] border border-[#E6DEC3] rounded-xl px-4 py-3 text-sm text-[#2C1A0C] placeholder:text-[#6E5B4F]/50 focus:outline-none focus:ring-2 focus:ring-[#2E4F3E]/40 focus:border-[#2E4F3E] transition";

const TABS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["leads", "Leads", Users],
  ["orders", "Orders", Package],
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dashboard");
  const [reloadKey, setReloadKey] = useState(0);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/stats`, auth(token)).catch((e) => {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#1C120C] flex items-center justify-center px-4">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm bg-[#FAF6EE]/5 border border-[#3D2B1E] rounded-3xl p-8 backdrop-blur"
        >
          <div className="flex flex-col items-center gap-3 mb-8">
            <img
              src="/images/vgt-logo.jpg"
              alt="Vanilla Global Transindo logo"
              className="w-16 h-16 rounded-full object-cover ring-1 ring-[#3D2B1E] shadow-lg"
            />
            <span className="font-serif font-bold text-[#FAF6EE]">VGT CRM</span>
          </div>
          <label htmlFor="admin-email" className="block font-mono text-[11px] tracking-[0.18em] uppercase text-[#D4A359] mb-2">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-login-email"
            type="email"
            required
            className={loginInputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@vanillaglobaltransindo.com"
          />
          <label htmlFor="admin-password" className="block font-mono text-[11px] tracking-[0.18em] uppercase text-[#D4A359] mt-5 mb-2">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-login-password"
            type="password"
            required
            className={loginInputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#D4A359] hover:bg-[#c2924a] disabled:opacity-60 text-[#1C120C] font-bold rounded-full px-6 py-3.5 text-sm transition-colors"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5EFE6]">
      <header className="bg-[#1C120C] text-[#FAF6EE] px-4 sm:px-8 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 sm:gap-5">
          <div className="flex items-center gap-2.5 mr-2">
            <img src="/images/vgt-logo.jpg" alt="VGT" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#3D2B1E]" />
            <span className="font-serif font-bold hidden sm:block">VGT CRM</span>
          </div>
          <nav className="flex gap-1.5">
            {TABS.map(([key, label, Icon]) => (
              <button
                key={key}
                data-testid={`admin-tab-${key}`}
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-4 py-2 transition-colors ${
                  tab === key ? "bg-[#D4A359] text-[#1C120C]" : "text-[#FAF6EE]/70 hover:text-[#FAF6EE]"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </nav>
          <div className="ml-auto flex gap-2">
            <button
              data-testid="admin-reload-button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FAF6EE]/70 hover:text-[#FAF6EE] rounded-full px-3 py-2 transition-colors"
            >
              <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FAF6EE]/70 hover:text-red-300 rounded-full px-3 py-2 transition-colors"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {tab === "dashboard" && <Dashboard token={token} reloadKey={reloadKey} />}
        {tab === "leads" && <LeadsTable token={token} reloadKey={reloadKey} onOpenDetail={setDetailId} />}
        {tab === "orders" && <Orders token={token} reloadKey={reloadKey} />}
      </main>
      {detailId && (
        <LeadDetail
          leadId={detailId}
          token={token}
          onClose={() => setDetailId(null)}
          onChanged={() => setReloadKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
